import type { FC, PropsWithChildren, ReactElement } from 'react';
import { Children, isValidElement, useMemo, useRef, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { Card, TouchableRipple } from 'react-native-paper';
import { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import ReanimatedCarousel from 'react-native-reanimated-carousel';

import { LeftIcon, RightIcon } from '@/assets';
import { DotPagination } from '@/shared/components/carousel/DotPagination';
import { isIOS, isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const ELEVATION_OFFSET = 8;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surfaceContainerLowest,
      marginHorizontal: 12
    },
    leftButton: {
      alignItems: 'flex-start',
      position: 'absolute',
      left: -22,
      height: 60,
      width: 60,
      borderRadius: 8
    },
    rightButton: {
      alignItems: 'flex-end',
      position: 'absolute',
      right: -22,
      height: 60,
      width: 60,
      borderRadius: 8
    }
  });

export const DynamicHeightCardCarousel: FC<PropsWithChildren> = ({
  children
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { width } = useWindowDimensions();

  const [viewWidth, setViewWidth] = useState<number>(width);
  const [heights, setHeights] = useState<Record<number, number>>({});
  const [buttonTop, setButtonTop] = useState(0);

  const progress = useSharedValue<number>(0);
  const ref = useRef<ICarouselInstance>(null);

  const items = useMemo(
    () =>
      Children.toArray(children).filter((child) =>
        isValidElement(child)
      ) as ReactElement[],
    [children]
  );

  const itemCount = items.length;

  const [prevItemCount, setPrevItemCount] = useState(itemCount);
  if (prevItemCount !== itemCount) {
    setPrevItemCount(itemCount);
    setHeights({});
    progress.set(0);
  }

  const handlePaginationPress = (index: number) => {
    ref.current?.scrollTo({
      count: index - progress.value,
      animated: true
    });
  };

  const carouselStyle = useAnimatedStyle(() => {
    if (itemCount === 0) return { height: 0, width: viewWidth };

    const currentIndex = Math.floor(progress.get());
    let nextIndex = Math.ceil(progress.get());
    if (nextIndex > itemCount - 1) nextIndex = 0;

    const currentHeight = heights[currentIndex];
    const nextHeight = heights[nextIndex];

    if (nextHeight === undefined) {
      return { height: 150 + ELEVATION_OFFSET, width: viewWidth };
    }

    if (!Number.isSafeInteger(progress.get()) && currentHeight !== undefined) {
      const height = Math.max(currentHeight, nextHeight) + ELEVATION_OFFSET;
      return { height, width: viewWidth };
    }

    const height = (nextHeight === 0 ? 150 : nextHeight) + ELEVATION_OFFSET;

    if (isWeb) setButtonTop(8 + (height - 60) / 2);

    return { height, width: viewWidth, minHeight: isIOS ? 150 : 0 };
  }, [itemCount, heights, progress, viewWidth]);

  const showArrows = isWeb && itemCount > 1;

  if (itemCount === 0) {
    // oxlint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <View
      style={{ overflow: 'hidden' }}
      onLayout={(e) => setViewWidth(Math.max(1, e.nativeEvent.layout.width))}
    >
      <ReanimatedCarousel
        ref={ref}
        key={itemCount}
        data={items}
        renderItem={({ item, index }) => (
          <View style={{ minHeight: isIOS ? 150 : 0 }}>
            <Card
              style={styles.card}
              onLayout={(e) => {
                const { height } = e.nativeEvent.layout;
                setHeights((prev) => ({
                  ...prev,
                  [index]: height
                }));
              }}
            >
              <Card.Content>{item}</Card.Content>
            </Card>
          </View>
        )}
        snapEnabled
        pagingEnabled
        loop={itemCount > 1}
        width={viewWidth}
        style={[{ marginTop: 12 }, carouselStyle as StyleProp<ViewStyle>]}
        onProgressChange={(_, p) => progress.set(p)}
      />
      <DotPagination
        progress={progress}
        size={itemCount}
        onPress={handlePaginationPress}
        color={theme.colors.onPrimary}
        style={{ marginVertical: 0 }}
      />
      {showArrows && (
        <>
          <TouchableRipple
            accessibilityRole="button"
            style={[styles.leftButton, { top: buttonTop }]}
            onPress={() => {
              handlePaginationPress(progress.get() - 1);
            }}
          >
            <LeftIcon
              style={{ marginLeft: 0 }}
              fill={theme.colors.onPrimary}
              stroke={theme.colors.primary}
              strokeWidth={10}
              width={60}
              height={60}
            />
          </TouchableRipple>
          <TouchableRipple
            accessibilityRole="button"
            style={[styles.rightButton, { top: buttonTop }]}
            onPress={() => {
              handlePaginationPress(progress.get() + 1);
            }}
          >
            <RightIcon
              style={{ marginRight: 0 }}
              fill={theme.colors.onPrimary}
              stroke={theme.colors.primary}
              strokeWidth={10}
              width={60}
              height={60}
            />
          </TouchableRipple>
        </>
      )}
    </View>
  );
};

DynamicHeightCardCarousel.displayName = 'DynamicHeightCardCarousel';
